import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertTriangle, Send, Lock, Calculator } from 'lucide-react';

const StudentTestComponent = ({ testId, onTestComplete, socket }) => {
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  
  const [testData, setTestData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [testStatus, setTestStatus] = useState('loading');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [violations, setViolations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lateJoinTime, setLateJoinTime] = useState(0);
  
  const testContainerRef = useRef(null);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const visibilityTimeoutRef = useRef(null);

  // Add CSS styles to head
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      .test-container {
        min-height: 100vh;
        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        display: flex;
        flex-direction: column;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
      }

      .security-bar {
        background: linear-gradient(135deg, #dc2626, #b91c1c);
        color: white;
        padding: 0.75rem 1.5rem;
        font-size: 0.875rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: 0 4px 20px rgba(220, 38, 38, 0.3);
      }

      .security-left {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .security-right {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .security-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 6px;
        font-size: 0.75rem;
        font-weight: 600;
      }

      .security-badge-blue {
        background: rgba(59, 130, 246, 0.2);
        border: 1px solid rgba(59, 130, 246, 0.3);
      }

      .security-badge-red {
        background: rgba(239, 68, 68, 0.2);
        border: 1px solid rgba(239, 68, 68, 0.3);
      }

      .header {
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(20px);
        border-bottom: 1px solid rgba(226, 232, 240, 0.5);
        padding: 1.5rem 2rem;
        box-shadow: 0 4px 25px rgba(0, 0, 0, 0.05);
      }

      .header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.5rem;
      }

      .header-title {
        font-size: 1.5rem;
        font-weight: 800;
        color: #1f2937;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0;
        letter-spacing: -0.025em;
      }

      .header-difficulty {
        font-size: 0.875rem;
        font-weight: 500;
        color: #6b7280;
        margin-left: 0.5rem;
      }

      .header-progress {
        color: #6b7280;
        font-size: 0.95rem;
        margin: 0.25rem 0 0;
        font-weight: 500;
      }

      .timer {
        font-size: 2rem;
        font-weight: 800;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.25rem;
      }

      .timer-normal {
        color: #3b82f6;
      }

      .timer-warning {
        color: #f59e0b;
      }

      .timer-critical {
        color: #ef4444;
        animation: pulse 1s infinite;
      }

      .timer-label {
        color: #6b7280;
        font-size: 0.875rem;
        margin: 0;
        font-weight: 500;
      }

      .progress-container {
        margin-top: 1rem;
      }

      .progress-bar {
        width: 100%;
        height: 8px;
        background: linear-gradient(135deg, #e5e7eb, #d1d5db);
        border-radius: 9999px;
        overflow: hidden;
      }

      .progress-fill {
        height: 100%;
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
        border-radius: 9999px;
        transition: width 0.3s ease;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.3);
      }

      .content {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
      }

      .question-card {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(226, 232, 240, 0.5);
        border-radius: 24px;
        padding: 3rem 2.5rem;
        width: 100%;
        max-width: 800px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
      }

      .question-section {
        margin-bottom: 3rem;
      }

      .question-text {
        font-size: 2.5rem;
        font-weight: 800;
        color: #1f2937;
        text-align: center;
        margin: 0 0 3rem;
        letter-spacing: -0.025em;
        background: linear-gradient(135deg, #1f2937, #374151);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .answer-section {
        width: 100%;
      }

      .options-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .option {
        display: flex;
        align-items: center;
        padding: 1.25rem 1.5rem;
        background: linear-gradient(135deg, #f8fafc, #f1f5f9);
        border: 2px solid transparent;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      }

      .option:hover {
        background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
        border-color: rgba(99, 102, 241, 0.3);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
      }

      .option-input {
        margin-right: 1rem;
        width: 1.25rem;
        height: 1.25rem;
        accent-color: #3b82f6;
      }

      .option-text {
        font-size: 1.125rem;
        font-weight: 500;
        color: #374151;
      }

      .input-container {
        text-align: center;
      }

      .answer-input {
        width: 100%;
        padding: 2rem 1.5rem;
        font-size: 2rem;
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
        font-weight: 600;
        text-align: center;
        background: linear-gradient(135deg, #ffffff, #f8fafc);
        border: 3px solid transparent;
        border-radius: 20px;
        outline: none;
        transition: all 0.3s ease;
        color: #1f2937;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
      }

      .answer-input:focus {
        border-color: #3b82f6;
        background: #ffffff;
        box-shadow: 0 12px 40px rgba(59, 130, 246, 0.2);
        transform: translateY(-2px);
      }

      .input-hint {
        margin: 1rem 0 0;
        color: #6b7280;
        font-size: 0.95rem;
        font-weight: 500;
      }

      .navigation {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
      }

      .nav-button {
        padding: 0.875rem 2rem;
        border-radius: 14px;
        font-weight: 600;
        font-size: 1rem;
        border: none;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .nav-button-prev {
        background: linear-gradient(135deg, #6b7280, #4b5563);
        color: white;
      }

      .nav-button-prev:hover:not(:disabled) {
        background: linear-gradient(135deg, #4b5563, #374151);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(107, 114, 128, 0.3);
      }

      .nav-button-prev:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
      }

      .nav-button-next {
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
        color: white;
      }

      .nav-button-next:hover {
        background: linear-gradient(135deg, #2563eb, #1e40af);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
      }

      .nav-button-submit {
        background: linear-gradient(135deg, #10b981, #047857);
        color: white;
      }

      .nav-button-submit:hover:not(:disabled) {
        background: linear-gradient(135deg, #059669, #065f46);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
      }

      .nav-button-submit:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }

      .submit-spinner {
        width: 1rem;
        height: 1rem;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top: 2px solid #ffffff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .progress-indicator {
        color: #6b7280;
        font-weight: 500;
        font-size: 0.95rem;
      }

      .footer {
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(20px);
        border-top: 1px solid rgba(226, 232, 240, 0.5);
        padding: 1.5rem 2rem;
      }

      .question-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        justify-content: center;
      }

      .question-number {
        width: 3rem;
        height: 3rem;
        border-radius: 12px;
        font-size: 0.95rem;
        font-weight: 600;
        border: 2px solid;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .question-number-current {
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
        color: white;
        border-color: #3b82f6;
        box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
      }

      .question-number-answered {
        background: linear-gradient(135deg, #d1fae5, #a7f3d0);
        color: #065f46;
        border-color: #10b981;
      }

      .question-number-unanswered {
        background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
        color: #6b7280;
        border-color: #d1d5db;
      }

      .question-number:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
      }

      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 1rem;
      }

      .modal-card {
        background: #ffffff;
        border-radius: 24px;
        padding: 2rem;
        max-width: 500px;
        width: 100%;
        box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
      }

      .modal-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1.5rem;
      }

      .modal-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #dc2626;
        margin: 0;
      }

      .modal-text {
        color: #374151;
        line-height: 1.6;
        margin: 0 0 1.5rem;
      }

      .violation-details {
        background: linear-gradient(135deg, #fef2f2, #fee2e2);
        border: 1px solid #fecaca;
        border-radius: 12px;
        padding: 1rem;
        margin-bottom: 2rem;
      }

      .violation-latest {
        color: #dc2626;
        font-size: 0.9rem;
        margin: 0 0 0.5rem;
      }

      .violation-count {
        color: #b91c1c;
        font-size: 0.9rem;
        margin: 0 0 0.5rem;
      }

      .violation-warning {
        color: #7f1d1d;
        font-size: 0.9rem;
        font-weight: 600;
        margin: 0.5rem 0 0;
      }

      .modal-actions {
        text-align: right;
      }

      .modal-button {
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
        color: white;
        padding: 0.75rem 2rem;
        border-radius: 12px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .modal-button:hover {
        background: linear-gradient(135deg, #2563eb, #1e40af);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
      }

      .loading-container {
        min-height: 100vh;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      }

      .loading-content {
        text-align: center;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 20px;
        padding: 3rem 2rem;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
      }

      .loading-spinner {
        width: 48px;
        height: 48px;
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-top: 4px solid #ffffff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 1.5rem;
      }

      .loading-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #ffffff;
        margin: 0 0 0.5rem;
        letter-spacing: -0.025em;
      }

      .loading-subtitle {
        color: rgba(255, 255, 255, 0.8);
        font-size: 0.95rem;
        margin: 0;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }

      @media (max-width: 768px) {
        .header {
          padding: 1rem 1.5rem;
        }
        
        .header-content {
          flex-direction: column;
          gap: 1rem;
          text-align: center;
        }
        
        .question-text {
          font-size: 1.75rem;
        }
        
        .answer-input {
          font-size: 1.5rem;
          padding: 1.5rem 1rem;
        }
        
        .navigation {
          flex-direction: column;
          gap: 1rem;
        }
        
        .nav-button {
          width: 100%;
          justify-content: center;
        }
        
        .question-grid {
          gap: 0.5rem;
        }
        
        .question-number {
          width: 2.5rem;
          height: 2.5rem;
          font-size: 0.875rem;
        }
      }

      @media (max-width: 480px) {
        .content {
          padding: 1rem;
        }
        
        .question-card {
          padding: 2rem 1.5rem;
        }
        
        .security-bar {
          padding: 0.5rem 1rem;
          font-size: 0.8rem;
        }
        
        .timer {
          font-size: 1.5rem;
        }
        
        .header-title {
          font-size: 1.25rem;
        }
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Helper for generating decimal twists
  const generateDecimalTwist = (difficulty) => {
    let digits = 1;
    if (difficulty === 'medium') digits = 2;
    if (difficulty === 'double' || difficulty === 'triple') digits = 2;
    
    const min = Math.pow(10, digits - 1);
    const max = Math.pow(10, digits) - 0.1;
    const count = 5; // Fixed sequence length for tests
    
    let currentTotal = 0;
    const numbers = [];
    
    for (let i = 0; i < count; i++) {
      let isNegative = i > 0 && Math.random() > 0.5;
      let nextNumber = parseFloat((Math.random() * (max - min) + min).toFixed(1));
      
      if (isNegative && currentTotal - nextNumber < 0) {
        isNegative = false;
      }
      
      numbers.push(`${isNegative ? '-' : i > 0 ? '+' : ''}${nextNumber}`);
      currentTotal += isNegative ? -nextNumber : nextNumber;
      currentTotal = parseFloat(currentTotal.toFixed(1));
    }
    
    return {
      question: `${numbers.join(' ')} = ?`,
      correctAnswer: currentTotal.toString()
    };
  };

  // Helper for generating mixed math
  const generateMixedMath = (difficulty) => {
    let range = 20;
    if (difficulty === 'medium') range = 50;
    if (difficulty === 'double') range = 99;
    
    let num1 = Math.floor(Math.random() * range) + 1;
    let num2 = Math.floor(Math.random() * range) + 1;
    const op = Math.random() < 0.5 ? 'addition' : 'subtraction';
    
    if (op === 'subtraction' && num1 < num2) [num1, num2] = [num2, num1];
    
    const question = op === 'addition' ? `${num1} + ${num2} = ?` : `${num1} - ${num2} = ?`;
    const correctAnswer = op === 'addition' ? (num1 + num2).toString() : (num1 - num2).toString();
    
    return { question, correctAnswer };
  };

  // Generate math questions based on test parameters
  const generateQuestions = (testType, count, difficultyLevel) => {
    const questions = [];
    
    for (let i = 0; i < count; i++) {
      let question, correctAnswer;
      let num1, num2;
      
      // Generate based on test type
      switch (testType) {
        case 'decimalRandomPlusMinus': {
          const twist = generateDecimalTwist(difficultyLevel);
          question = twist.question;
          correctAnswer = twist.correctAnswer;
          break;
        }
        case 'mixedMath':
        case 'customDigitMixedMath': {
          const mixed = generateMixedMath(difficultyLevel);
          question = mixed.question;
          correctAnswer = mixed.correctAnswer;
          break;
        }
        case 'multiplication': {
          const range = difficultyLevel === 'easy' ? 10 : difficultyLevel === 'medium' ? 50 : 99;
          num1 = Math.floor(Math.random() * range) + 1;
          num2 = Math.floor(Math.random() * range) + 1;
          question = `${num1} × ${num2} = ?`;
          correctAnswer = (num1 * num2).toString();
          break;
        }
        case 'addition': {
          const range = difficultyLevel === 'easy' ? 20 : difficultyLevel === 'medium' ? 100 : 500;
          num1 = Math.floor(Math.random() * range) + 1;
          num2 = Math.floor(Math.random() * range) + 1;
          question = `${num1} + ${num2} = ?`;
          correctAnswer = (num1 + num2).toString();
          break;
        }
        case 'subtraction': {
          const range = difficultyLevel === 'easy' ? 20 : difficultyLevel === 'medium' ? 100 : 500;
          num1 = Math.floor(Math.random() * range) + 1;
          num2 = Math.floor(Math.random() * range) + 1;
          if (num1 < num2) [num1, num2] = [num2, num1];
          question = `${num1} - ${num2} = ?`;
          correctAnswer = (num1 - num2).toString();
          break;
        }
        case 'division': {
          const range = difficultyLevel === 'easy' ? 10 : difficultyLevel === 'medium' ? 20 : 50;
          num1 = Math.floor(Math.random() * range) + 1;
          num2 = Math.floor(Math.random() * range) + 1;
          const product = num1 * num2;
          question = `${product} ÷ ${num1} = ?`;
          correctAnswer = num2.toString();
          break;
        }
        default: {
          num1 = Math.floor(Math.random() * 10) + 1;
          num2 = Math.floor(Math.random() * 10) + 1;
          question = `${num1} + ${num2} = ?`;
          correctAnswer = (num1 + num2).toString();
        }
      }
      
      questions.push({
        question,
        correctAnswer,
        type: testType,
        options: []
      });
    }
    
    return questions;
  };

  // Fetch test data from your existing backend
  useEffect(() => {
    const fetchTestData = async () => {
      try {
        const token = localStorage.getItem('token');
        const deviceId = localStorage.getItem('deviceId');

        if (!token) {
          setTestStatus('error');
          return;
        }

        // Fetch the test details first
        const response = await fetch(`${API_URL}/student/tests/${testId}`, {
          headers: {
            'Authorization': token,
            'X-Device-Id': deviceId,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            setTestStatus('no-test');
            console.warn('Test not found or inactive');
            return;
          }
          throw new Error('Failed to load test');
        }

        const testDetails = await response.json();
        
        // Check if test is still active and not past end time
        const now = new Date();
        const testStart = new Date(testDetails.startTime);
        const testEnd = new Date(testStart.getTime() + (testDetails.duration * 60 * 1000));
        
        if (now > testEnd) {
          setTestStatus('test-ended');
          return;
        }

        // Check if student already submitted
        const userId = localStorage.getItem('userId') || 'anonymous';
        const existingResult = testDetails.results?.find(r => r.studentId === userId);
        if (existingResult) {
          setTestStatus('already-submitted');
          return;
        }

        // Calculate late join time if applicable
        let adjustedTimeRemaining = testDetails.duration * 60; // Convert to seconds
        let lateJoinSeconds = 0;
        
        if (now > testStart) {
          lateJoinSeconds = Math.floor((now - testStart) / 1000);
          adjustedTimeRemaining = Math.max(0, adjustedTimeRemaining - lateJoinSeconds);
          setLateJoinTime(lateJoinSeconds);
        }

        // Generate questions if not provided
        let questions = testDetails.questions || [];
        if (questions.length === 0) {
          questions = generateQuestions(
            testDetails.testType, 
            testDetails.count, 
            testDetails.difficultyLevel
          );
        }

        const processedTestData = {
          ...testDetails,
          questions,
          gameType: testDetails.testType // For compatibility
        };

        setTestData(processedTestData);
        setTimeRemaining(adjustedTimeRemaining);
        setAnswers(new Array(questions.length).fill(''));
        setTestStatus('active');
        startTimeRef.current = Date.now();
        
        // Notify admin via socket
        socket?.emit('test-started', { 
          testId: testDetails._id,
          studentId: userId,
          lateJoinSeconds
        });

      } catch (error) {
        console.error('Error loading test:', error);
        setTestStatus('error');
      }
    };

    if (testId) {
      fetchTestData();
    }
  }, [testId, socket, API_URL]);

  // Enhanced fullscreen handling
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (testContainerRef.current && !isFullscreen) {
          if (testContainerRef.current.requestFullscreen) {
            await testContainerRef.current.requestFullscreen();
          } else if (testContainerRef.current.webkitRequestFullscreen) {
            await testContainerRef.current.webkitRequestFullscreen();
          } else if (testContainerRef.current.msRequestFullscreen) {
            await testContainerRef.current.msRequestFullscreen();
          }
          setIsFullscreen(true);
        }
      } catch (error) {
        console.warn('Fullscreen not supported:', error);
        // Continue test even without fullscreen
      }
    };

    if (testStatus === 'active' && testContainerRef.current) {
      enterFullscreen();
    }
  }, [testStatus]);

  // Handle fullscreen changes with stricter enforcement
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = Boolean(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
      
      if (!isCurrentlyFullscreen && testStatus === 'active') {
        addViolation('Exited fullscreen mode');
        
        // Try to re-enter fullscreen immediately
        if (testContainerRef.current) {
          const enterFullscreen = async () => {
            try {
              if (testContainerRef.current.requestFullscreen) {
                await testContainerRef.current.requestFullscreen();
              } else if (testContainerRef.current.webkitRequestFullscreen) {
                await testContainerRef.current.webkitRequestFullscreen();
              }
            } catch (error) {
              // If can't re-enter after 3 violations, auto-submit
              if (violations.length >= 2) {
                handleAutoSubmit('Multiple fullscreen violations');
              }
            }
          };
          enterFullscreen();
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [testStatus, violations]);

  // Enhanced visibility handling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && testStatus === 'active') {
        addViolation('Switched to another tab/window');
        
        // Shorter timeout for tab switching - 3 seconds
        visibilityTimeoutRef.current = setTimeout(() => {
          if (document.hidden) {
            handleAutoSubmit('Tab switching violation - remained hidden');
          }
        }, 3000);
      } else if (!document.hidden && visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [testStatus]);

  // Enhanced browser navigation prevention
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (testStatus === 'active') {
        event.preventDefault();
        event.returnValue = 'Your test is in progress. Leaving will auto-submit your test.';
        return event.returnValue;
      }
    };

    const handlePopState = (event) => {
      if (testStatus === 'active') {
        event.preventDefault();
        window.history.pushState(null, '', window.location.pathname);
        addViolation('Browser navigation attempt');
        handleAutoSubmit('Navigation attempt during test');
      }
    };

    // Push current state to prevent back navigation
    window.history.pushState(null, '', window.location.pathname);
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [testStatus]);

  // Timer countdown with auto-submit
  useEffect(() => {
    if (testStatus === 'active' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleAutoSubmit('Time expired');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [testStatus, timeRemaining]);

  // Send progress updates to admin
  // useEffect(() => {
  //   if (testStatus === 'active' && socket) {
  //     const progressInterval = setInterval(() => {
  //       const answeredCount = answers.filter(a => a.trim() !== '').length;
  //       const progress = (answeredCount / answers.length) * 100;
        
  //       socket.emit('test-progress', {
  //         testId,
  //         studentId: localStorage.getItem('userId') || 'anonymous',
  //         progress: Math.round(progress),
  //         currentQuestion: currentQuestionIndex + 1,
  //         answeredQuestions: answeredCount,
  //         timeRemaining
  //       });
  //     }, 5000);

  //     return () => clearInterval(progressInterval);
  //   }
  // }, [testStatus, answers, currentQuestionIndex, socket, testId, timeRemaining]);

  const addViolation = (violation) => {
    setViolations(prev => {
      const newViolations = [...prev, {
        type: violation,
        timestamp: new Date().toISOString()
      }];
      
      // Auto-submit after 3 violations
      if (newViolations.length >= 3) {
        setTimeout(() => handleAutoSubmit('Maximum violations exceeded'), 100);
      }
      
      return newViolations;
    });
  };

  const handleAutoSubmit = async (reason) => {
    if (isSubmitting) return;
    
    console.log(`Auto-submitting test: ${reason}`);
    await submitTest(true, reason);
  };

  const handleAnswerChange = (questionIndex, value) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = value.trim();
    setAnswers(newAnswers);
  };

  const calculateScore = () => {
    if (!testData) return { correct: 0, total: 0, score: 0 };
    
    let correct = 0;
    testData.questions.forEach((question, index) => {
      const userAnswer = answers[index]?.trim().toLowerCase();
      const correctAnswer = question.correctAnswer?.trim().toLowerCase();
      
      if (userAnswer === correctAnswer) {
        correct++;
      }
    });
    
    const total = testData.questions.length;
    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    
    return { correct, total, score };
  };

  // const submitTest = async (isAutoSubmit = false, reason = '') => {
  //   if (isSubmitting) return;
    
  //   setIsSubmitting(true);
    
  //   try {
  //     const { correct, total, score } = calculateScore();
  //     const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
  //     const token = localStorage.getItem('token');
  //     const deviceId = localStorage.getItem('deviceId');
  //     const userId = localStorage.getItem('userId') || 'anonymous';

  //     const submissionData = {
  //       studentId: userId,
  //       answers: answers.map((answer, index) => ({
  //         questionIndex: index,
  //         question: testData.questions[index].question,
  //         userAnswer: answer,
  //         correctAnswer: testData.questions[index].correctAnswer,
  //         isCorrect: answer.trim().toLowerCase() === testData.questions[index].correctAnswer.trim().toLowerCase()
  //       })),
  //       correctCount: correct,
  //       totalQuestions: total,
  //       score,
  //       timeSpent,
  //       lateJoinTime,
  //       violations,
  //       isAutoSubmit,
  //       autoSubmitReason: reason,
  //       submittedAt: new Date().toISOString()
  //     };

  //     const response = await fetch(`${API_URL}/student/tests/${testId}/submit`, {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': token,
  //         'X-Device-Id': deviceId,
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify(submissionData)
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Submission failed: ${response.status}`);
  //     }

  //     // Update performance data if API exists
  //     try {
  //       const performanceUpdate = {
  //         totalGames: 1,
  //         totalCorrect: correct,
  //         totalScore: score,
  //         gameType: testData.testType,
  //         difficulty: testData.difficultyLevel || 'medium',
  //         speed: 2,
  //         count: total
  //       };

  //       await fetch(`${API_URL}/performance/${userId}`, {
  //         method: 'POST',
  //         headers: {
  //           'Authorization': token,
  //           'X-Device-Id': deviceId,
  //           'Content-Type': 'application/json'
  //         },
  //         body: JSON.stringify(performanceUpdate)
  //       });
  //     } catch (perfError) {
  //       console.warn('Performance update failed:', perfError);
  //       // Continue even if performance update fails
  //     }

  //     // Notify admin via socket
  //     socket?.emit('test-submitted', {
  //       testId,
  //       studentId: userId,
  //       score,
  //       correctCount: correct,
  //       totalQuestions: total,
  //       isAutoSubmit,
  //       reason,
  //       timeSpent,
  //       violations: violations.length
  //     });

  //     setTestStatus('submitted');

  //     // Exit fullscreen
  //     if (document.fullscreenElement) {
  //       document.exitFullscreen();
  //     } else if (document.webkitFullscreenElement) {
  //       document.webkitExitFullscreen();
  //     } else if (document.msExitFullscreen) {
  //       document.msExitFullscreen();
  //     }

  //     // Call completion callback
  //     onTestComplete?.({
  //       score,
  //       correct,
  //       total,
  //       isAutoSubmit,
  //       reason,
  //       timeSpent,
  //       violations: violations.length
  //     });

  //   } catch (error) {
  //     console.error('Error submitting test:', error);
      
  //     // Fallback: save to localStorage
  //     try {
  //       const { correct, total, score } = calculateScore();
  //       const fallbackData = {
  //         testId,
  //         score,
  //         correct,
  //         total,
  //         submittedAt: new Date().toISOString(),
  //         isAutoSubmit,
  //         reason
  //       };
        
  //       const existingFallbacks = JSON.parse(localStorage.getItem('testSubmissionFallbacks') || '[]');
  //       existingFallbacks.push(fallbackData);
  //       localStorage.setItem('testSubmissionFallbacks', JSON.stringify(existingFallbacks));
        
  //       console.log('Test saved to localStorage as fallback');
  //     } catch (localError) {
  //       console.error('Error saving fallback:', localError);
  //     }
      
  //     setTestStatus('error');
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  // ✅ FIXED: Test submission with retry logic and better error handling
const submitTest = async (isAutoSubmit = false, reason = '') => {
  if (isSubmitting) return;
  
  setIsSubmitting(true);
  
  try {
    const { correct, total, score } = calculateScore();
    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
    const token = localStorage.getItem('token');
    const deviceId = localStorage.getItem('deviceId');
    const userId = localStorage.getItem('userId') || 'anonymous';

    console.log('📤 Submitting test:', { testId, score, correctCount: correct, timeSpent });

    // ✅ Prepare minimal submission data
    const submissionData = {
      studentId: userId,
      answers: answers.map((answer, index) => ({
        questionIndex: index,
        question: testData.questions[index].question,
        userAnswer: answer,
        correctAnswer: testData.questions[index].correctAnswer,
        isCorrect: answer.trim().toLowerCase() === testData.questions[index].correctAnswer.trim().toLowerCase()
      })),
      correctCount: correct,
      totalQuestions: total,
      score,
      timeSpent,
      lateJoinTime,
      violations,
      isAutoSubmit,
      autoSubmitReason: reason,
      submittedAt: new Date().toISOString()
    };

    // ✅ Retry logic with exponential backoff
    let attempt = 0;
    const maxAttempts = 3;
    let lastError = null;

    while (attempt < maxAttempts) {
      try {
        console.log(`🔄 Submission attempt ${attempt + 1}/${maxAttempts}`);

        // ✅ Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout

        const response = await fetch(`${API_URL}/student/tests/${testId}/submit`, {
          method: 'POST',
          headers: {
            'Authorization': token,
            'X-Device-Id': deviceId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submissionData),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Submission failed: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Test submitted successfully:', result);

        // ✅ Success - notify admin via socket
        socket?.emit('test-submitted', {
          testId,
          studentId: userId,
          score,
          correctCount: correct,
          totalQuestions: total,
          isAutoSubmit,
          reason,
          timeSpent,
          violations: violations.length
        });

        setTestStatus('submitted');

        // Exit fullscreen
        try {
          if (document.fullscreenElement) {
            await document.exitFullscreen();
          } else if (document.webkitFullscreenElement) {
            await document.webkitExitFullscreen();
          }
        } catch (fsError) {
          console.warn('Fullscreen exit error:', fsError);
        }

        // Call completion callback
        onTestComplete?.({
          score,
          correct,
          total,
          isAutoSubmit,
          reason,
          timeSpent,
          violations: violations.length
        });

        return; // Success - exit function

      } catch (fetchError) {
        lastError = fetchError;
        console.error(`❌ Attempt ${attempt + 1} failed:`, fetchError.message);

        if (fetchError.name === 'AbortError') {
          console.log('⏱️ Request timed out');
        }

        // Don't retry on certain errors
        if (fetchError.message.includes('already submitted')) {
          console.log('⚠️ Test already submitted - treating as success');
          setTestStatus('submitted');
          onTestComplete?.({ score, correct, total, isAutoSubmit, reason, timeSpent, violations: violations.length });
          return;
        }

        attempt++;
        
        if (attempt < maxAttempts) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`⏳ Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // ✅ All retries failed - save to localStorage as fallback
    throw new Error(lastError?.message || 'Max retries exceeded');

  } catch (error) {
    console.error('❌ Final submission error:', error);
    
    // ✅ Fallback: Save to localStorage
    try {
      const { correct, total, score } = calculateScore();
      const fallbackData = {
        testId,
        studentId: localStorage.getItem('userId'),
        score,
        correctCount: correct,
        totalQuestions: total,
        answers: answers.map((answer, index) => ({
          question: testData.questions[index].question,
          userAnswer: answer,
          correctAnswer: testData.questions[index].correctAnswer
        })),
        submittedAt: new Date().toISOString(),
        isAutoSubmit,
        reason,
        timeSpent: Math.round((Date.now() - startTimeRef.current) / 1000),
        violations,
        status: 'pending-sync'
      };
      
      const existingFallbacks = JSON.parse(localStorage.getItem('testSubmissionFallbacks') || '[]');
      existingFallbacks.push(fallbackData);
      localStorage.setItem('testSubmissionFallbacks', JSON.stringify(existingFallbacks));
      
      console.log('💾 Test saved to localStorage as fallback');
      
      // Show user-friendly message
      alert(
        'Test saved locally!\n\n' +
        'Your answers have been saved on this device. ' +
        'Please inform your teacher about this submission.\n\n' +
        `Score: ${score}% (${correct}/${total} correct)`
      );
      
      setTestStatus('submitted');
      onTestComplete?.({ score, correct, total, isAutoSubmit, reason, timeSpent: fallbackData.timeSpent, violations: violations.length });
      
    } catch (localError) {
      console.error('❌ Error saving fallback:', localError);
      setTestStatus('error');
    }
  } finally {
    setIsSubmitting(false);
  }
};

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Enhanced keyboard and mouse prevention
  useEffect(() => {
    const preventContextMenu = (e) => {
      if (testStatus === 'active') {
        e.preventDefault();
        addViolation('Right-click attempt');
        return false;
      }
    };

    const preventKeyboardShortcuts = (e) => {
      if (testStatus === 'active') {
        // Prevent common shortcuts
        if (e.ctrlKey || e.metaKey || e.altKey) {
          e.preventDefault();
          addViolation('Keyboard shortcut attempt');
          return false;
        }
        
        // Prevent function keys
        if (e.key.startsWith('F') && e.key.length > 1) {
          e.preventDefault();
          addViolation('Function key attempt');
          return false;
        }
      }
    };

    const preventSelection = (e) => {
      if (testStatus === 'active') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyboardShortcuts);
    document.addEventListener('selectstart', preventSelection);
    document.addEventListener('dragstart', preventSelection);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
      document.removeEventListener('selectstart', preventSelection);
      document.removeEventListener('dragstart', preventSelection);
    };
  }, [testStatus]);

  // Status screens
  if (testStatus === 'loading') {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2 className="loading-title">Loading Test...</h2>
          <p className="loading-subtitle">Preparing your exam questions</p>
        </div>
      </div>
    );
  }

  if (testStatus === 'no-test') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
        <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '24px', padding: '3rem 2rem', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
          <Clock style={{ width: '4rem', height: '4rem', color: '#3b82f6', margin: '0 auto 1.5rem', display: 'block' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.75rem', letterSpacing: '-0.025em' }}>Test Not Found</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem', lineHeight: 1.6 }}>The requested test could not be found or is not available.</p>
          <button onClick={() => window.history.back()} style={{ padding: '0.75rem 2rem', borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'test-ended') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
        <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '24px', padding: '3rem 2rem', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
          <AlertTriangle style={{ width: '4rem', height: '4rem', color: '#f59e0b', margin: '0 auto 1.5rem', display: 'block' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.75rem', letterSpacing: '-0.025em' }}>Test Time Expired</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem', lineHeight: 1.6 }}>The time limit for this test has already passed.</p>
          <button onClick={() => window.history.back()} style={{ padding: '0.75rem 2rem', borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'already-submitted') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
        <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '24px', padding: '3rem 2rem', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
          <Send style={{ width: '4rem', height: '4rem', color: '#10b981', margin: '0 auto 1.5rem', display: 'block' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.75rem', letterSpacing: '-0.025em' }}>Test Already Submitted</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem', lineHeight: 1.6 }}>You have already completed this test successfully.</p>
          <button onClick={() => window.history.back()} style={{ padding: '0.75rem 2rem', borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
        <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '24px', padding: '3rem 2rem', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
          <AlertTriangle style={{ width: '4rem', height: '4rem', color: '#ef4444', margin: '0 auto 1.5rem', display: 'block' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626', margin: '0 0 0.75rem', letterSpacing: '-0.025em' }}>Test Error</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem', lineHeight: 1.6 }}>Unable to load or submit the test. Please contact your teacher.</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => window.location.reload()} style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white' }}>
              Try Again
            </button>
            <button onClick={() => window.history.back()} style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 600, border: '1px solid rgba(107, 114, 128, 0.3)', cursor: 'pointer', transition: 'all 0.2s', background: 'rgba(107, 114, 128, 0.1)', color: '#374151' }}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (testStatus === 'submitted') {
    const { correct, total, score } = calculateScore();
    
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
        <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '24px', padding: '3rem 2rem', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
          <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', background: 'rgba(0, 0, 0, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem' }}>
            <Send style={{ color: '#10b981', width: '2rem', height: '2rem' }} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: '0 0 1rem' }}>Test Submitted Successfully!</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem' }}>Your test has been submitted and saved.</p>
          
          <div style={{ background: 'rgba(0, 0, 0, 0.05)', borderRadius: '12px', padding: '1rem', margin: '0 0 1.5rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 600, color: '#10b981', marginBottom: '0.5rem' }}>{score}%</div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {correct} out of {total} questions correct
            </div>
          </div>
          
          {lateJoinTime > 0 && (
            <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', padding: '0.75rem', margin: '0 0 1rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
                You joined {Math.floor(lateJoinTime / 60)}:{(lateJoinTime % 60).toString().padStart(2, '0')} late
              </p>
            </div>
          )}
          
          {violations.length > 0 && (
            <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', padding: '0.75rem', margin: '0 0 1rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                {violations.length} violation{violations.length > 1 ? 's' : ''} recorded
              </p>
            </div>
          )}
          
          <button onClick={() => window.history.back()} style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', padding: '0.75rem 2rem', borderRadius: '12px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}>   
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!testData) return null;

  const currentQuestion = testData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / testData.questions.length) * 100;

  return (
    <div 
      ref={testContainerRef}
      className="test-container"
    >
      {/* Security Status Bar */}
      <div className="security-bar">
        <div className="security-left">
          <Lock style={{ width: '1rem', height: '1rem' }} />
          <span style={{ fontWeight: 500 }}>EXAM MODE - Do not close window, switch tabs, or use keyboard shortcuts</span>
        </div>
        <div className="security-right">
          {lateJoinTime > 0 && (
            <span className="security-badge security-badge-blue">
              Late: +{Math.floor(lateJoinTime / 60)}:{(lateJoinTime % 60).toString().padStart(2, '0')}
            </span>
          )}
          {violations.length > 0 && (
            <span className="security-badge security-badge-red">
              {violations.length} violation(s)
            </span>
          )}
        </div>
      </div>

      {/* Timer and Progress Header */}
      <div className="header">
        <div className="header-content">
          <div>
            <h1 className="header-title">
              <Calculator style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
              {testData.testType.charAt(0).toUpperCase() + testData.testType.slice(1)} Test
              <span className="header-difficulty">
                ({testData.difficultyLevel})
              </span>
            </h1>
            <p className="header-progress">
              Question {currentQuestionIndex + 1} of {testData.questions.length}
            </p>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <div className={`timer ${
              timeRemaining < 60 ? 'timer-critical' : 
              timeRemaining < 300 ? 'timer-warning' : 'timer-normal'
            }`}>
              <Clock style={{ width: '1.5rem', height: '1.5rem' }} />
              {formatTime(timeRemaining)}
            </div>
            <p className="timer-label">Time remaining</p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="content">
        <div className="question-card">
          <div className="question-section">
            <h2 className="question-text">
              {currentQuestion.question}
            </h2>
            
            {/* Answer Input */}
            <div className="answer-section">
              {currentQuestion.options && currentQuestion.options.length > 0 ? (
                // Multiple choice
                <div className="options-container">
                  {currentQuestion.options.map((option, index) => (
                    <label 
                      key={index}
                      className="option"
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestionIndex}`}
                        value={option}
                        checked={answers[currentQuestionIndex] === option}
                        onChange={(e) => handleAnswerChange(currentQuestionIndex, e.target.value)}
                        className="option-input"
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  ))}
                </div>
              ) : (
                // Text input for math problems
                <div className="input-container">
                  <input
                    type="text"
                    value={answers[currentQuestionIndex]}
                    onChange={(e) => handleAnswerChange(currentQuestionIndex, e.target.value)}
                    className="answer-input"
                    placeholder="Enter your answer"
                    autoFocus
                    onKeyPress={(e) => {
                      // Allow only numbers and basic math symbols
                      const allowedChars = /[0-9\-+.]/;
                      if (!allowedChars.test(e.key) && e.key !== 'Backspace' && e.key !== 'Enter') {
                        e.preventDefault();
                      }
                      // Submit on Enter if it's the last question
                      if (e.key === 'Enter' && currentQuestionIndex === testData.questions.length - 1) {
                        submitTest(false);
                      }
                    }}
                  />
                  <p className="input-hint">
                    Enter numbers only. Press Enter to move to next question.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="navigation">
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              className={`nav-button nav-button-prev ${currentQuestionIndex === 0 ? '' : ''}`}
            >
              Previous
            </button>

            <span className="progress-indicator">
              {answers.filter(a => a.trim() !== '').length} of {testData.questions.length} answered
            </span>

            {currentQuestionIndex < testData.questions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                className="nav-button nav-button-next"
              >
                Next
              </button>
            ) : (
              <button
                onClick={() => submitTest(false)}
                disabled={isSubmitting}
                className="nav-button nav-button-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="submit-spinner"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send style={{ width: '1rem', height: '1rem' }} />
                    Submit Test
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Question Navigation Footer */}
      <div className="footer">
        <div className="question-grid">
          {testData.questions.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentQuestionIndex(index)}
              className={`question-number ${
                index === currentQuestionIndex
                  ? 'question-number-current'
                  : answers[index]?.trim()
                  ? 'question-number-answered'
                  : 'question-number-unanswered'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Warning Modal for Violations */}
      {violations.length > 0 && violations.length % 2 === 1 && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <AlertTriangle style={{ width: '2rem', height: '2rem', color: '#ef4444' }} />
              <h3 className="modal-title">Security Violation Detected</h3>
            </div>
            <p className="modal-text">
              You have attempted to leave the test environment or use prohibited features. 
              This violation has been recorded.
            </p>
            <div className="violation-details">
              <p className="violation-latest">
                <strong>Latest Violation:</strong> {violations[violations.length - 1]?.type}
              </p>
              <p className="violation-count">
                Total violations: {violations.length}
              </p>
              {violations.length >= 2 && (
                <p className="violation-warning">
                  Warning: One more violation will auto-submit your test!
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  // Modal will auto-close, violation is already recorded
                }}
                className="modal-button"
              >
                Continue Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TestContainer = ({ studentId, socket }) => {
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const [activeTest, setActiveTest] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [timeToStart, setTimeToStart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for active tests using your existing API structure
  useEffect(() => {
    const checkForActiveTests = async () => {
      try {
        if (!activeTest) setLoading(true);
        setError(null);
        
        // Helper to clean token (remove quotes if stored as JSON)
        const cleanToken = (t) => {
          if (!t) return t;
          if (t.startsWith('"') && t.endsWith('"')) return t.slice(1, -1);
          return t;
        };

        const token = cleanToken(localStorage.getItem('token'));
        const deviceId = localStorage.getItem('deviceId');
        
        if (!token) {
          console.warn('Test Polling: No token found');
          setLoading(false);
          return;
        }

        const userStr = localStorage.getItem('user');
        let currentUserId = null;
        try {
          if (userStr) currentUserId = JSON.parse(userStr)?._id || JSON.parse(userStr)?.id;
        } catch (e) {}

        const response = await fetch(`${API_URL}/student/tests/active`, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'X-Device-Id': deviceId,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error(`Failed to fetch tests: ${response.status}`);

        const tests = await response.json();
        console.log('--- TEST FETCH DEBUG ---');
        console.log('API Endpoint:', `${API_URL}/student/tests/active`);
        console.log('Raw Tests received:', tests);
        console.log('Current Student ID:', currentUserId);
        
        if (tests && tests.length > 0) {
          const currentTime = new Date();
          const availableTests = tests.filter(test => {
            const testStart = new Date(test.startTime);
            // Show tests starting in the future (up to 1 hour ahead) or currently running
            const oneHourAhead = new Date(currentTime.getTime() + (60 * 60 * 1000));
            const testEnd = new Date(testStart.getTime() + (test.duration * 60 * 1000));
            
            return currentTime < testEnd && testStart < oneHourAhead;
          });
          
          if (availableTests.length > 0) {
            // Sort by start time, earliest first
            availableTests.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
            setActiveTest(availableTests[0]);
          } else {
            setActiveTest(null);
          }
        } else {
          setActiveTest(null);
        }
      } catch (error) {
        console.error('Error checking for active tests:', error);
        // Don't show error if we already have a test (silent background fail)
        if (!activeTest) setError(`Error loading tests: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    checkForActiveTests();

    // Check every 10 seconds for new tests (faster than 30s)
    const interval = setInterval(checkForActiveTests, 10000);
    return () => clearInterval(interval);
  }, [API_URL, activeTest]);

  // Countdown timer effect
  useEffect(() => {
    if (!activeTest) {
      setTimeToStart(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const start = new Date(activeTest.startTime);
      const diff = start.getTime() - now.getTime();

      if (diff > 0) {
        // Format as MM:SS
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeToStart(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setTimeToStart(null);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeTest]);

  // Listen for test events from admin (if socket is available)
  useEffect(() => {
    if (socket) {
      const handleTestLaunched = (testData) => {
        console.log('Test launched event received:', testData);
        setActiveTest(testData);
      };

      const handleTestEnded = (data) => {
        console.log('Test ended event received:', data);
        if (activeTest && activeTest._id === data.testId) {
          setActiveTest(null);
          setShowTest(false);
        }
      };

      socket.on('test-launched', handleTestLaunched);
      socket.on('test-ended', handleTestEnded);

      return () => {
        socket.off('test-launched', handleTestLaunched);
        socket.off('test-ended', handleTestEnded);
      };
    }
  }, [socket, activeTest]);

  const handleStartTest = () => {
    console.log('Starting test:', activeTest);
    setShowTest(true);
  };

  const handleTestComplete = (result) => {
    console.log('Test completed:', result);
    setTestResult(result);
    setShowTest(false);
    setActiveTest(null);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
        <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(226, 232, 240, 0.5)', borderRadius: '20px', padding: '2rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid rgba(59, 130, 246, 0.3)', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#374151', fontWeight: 500, margin: '0 0 0.25rem' }}>Checking for active tests...</p>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
        <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(254, 202, 202, 0.5)', borderRadius: '20px', padding: '2rem', textAlign: 'center', maxWidth: '400px', boxShadow: '0 10px 40px rgba(239, 68, 68, 0.1)' }}>
          <AlertTriangle style={{ width: '3rem', height: '3rem', color: '#ef4444', margin: '0 auto 1rem' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626', margin: '0 0 0.5rem' }}>Error</h2>
          <p style={{ color: '#6b7280', margin: '0 0 2rem', lineHeight: 1.5 }}>{error}</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white' }}
            >
              Retry
            </button>
            <button
              onClick={() => setError(null)}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 600, border: '1px solid rgba(107, 114, 128, 0.3)', cursor: 'pointer', transition: 'all 0.2s', background: 'rgba(107, 114, 128, 0.1)', color: '#374151' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showTest && activeTest) {
    return (
      <StudentTestComponent
        testId={activeTest._id || activeTest.id}
        onTestComplete={handleTestComplete}
        socket={socket}
      />
    );
  }

  if (testResult) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dcfdf7 0%, #a7f3d0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
        <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(167, 243, 208, 0.5)', borderRadius: '20px', padding: '2rem', textAlign: 'center', maxWidth: '400px', boxShadow: '0 10px 40px rgba(16, 185, 129, 0.1)' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46', margin: '0 0 1rem' }}>Test Complete!</h2>
          <div style={{ fontSize: '3rem', fontWeight: 800, background: 'linear-gradient(135deg, #059669, #047857)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', margin: '0 0 0.5rem' }}>
            {testResult.score}%
          </div>
          <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>
            You answered {testResult.correct} out of {testResult.total} questions correctly.
          </p>
          {testResult.isAutoSubmit && (
            <div style={{ borderRadius: '10px', padding: '0.75rem', margin: '0 0 1rem', border: '1px solid rgba(245, 158, 11, 0.3)', background: 'rgba(245, 158, 11, 0.1)' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#92400e' }}>
                <strong>Note:</strong> Test was auto-submitted due to: {testResult.reason}
              </p>
            </div>
          )}
          {testResult.violations > 0 && (
            <div style={{ borderRadius: '10px', padding: '0.75rem', margin: '0 0 1rem', border: '1px solid rgba(239, 68, 68, 0.3)', background: 'rgba(239, 68, 68, 0.1)' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#b91c1c' }}>
                <strong>Security Violations:</strong> {testResult.violations} recorded
              </p>
            </div>
          )}
          <button
            onClick={() => setTestResult(null)}
            style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', padding: '0.875rem 2rem', borderRadius: '12px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (activeTest) {
    const now = new Date();
    const testStart = new Date(activeTest.startTime);
    const testEnd = new Date(testStart.getTime() + (activeTest.duration * 60 * 1000));
    const isTestTime = now >= testStart && now < testEnd;
    const isTestOver = now >= testEnd;
    
    console.log('Active test found:', {
      testId: activeTest._id,
      now: now.toISOString(),
      start: testStart.toISOString(),
      end: testEnd.toISOString(),
      isTestTime,
      isTestOver
    });
    
    if (isTestOver) {
      return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(253, 230, 138, 0.5)', borderRadius: '20px', padding: '2rem', textAlign: 'center', maxWidth: '400px', boxShadow: '0 10px 40px rgba(245, 158, 11, 0.1)' }}>
            <AlertTriangle style={{ width: '4rem', height: '4rem', color: '#f59e0b', margin: '0 auto 1rem' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#92400e', margin: '0 0 0.5rem' }}>Test Expired</h2>
            <p style={{ color: '#6b7280', margin: '0 0 2rem' }}>The time limit for this test has passed.</p>
            <button 
              onClick={() => setActiveTest(null)} 
              style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', padding: '0.75rem 2rem', borderRadius: '12px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}
            >
              Continue
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
        <div style={{ background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(191, 219, 254, 0.5)', borderRadius: '24px', padding: '2.5rem', textAlign: 'center', maxWidth: '500px', width: '100%', boxShadow: '0 20px 50px rgba(59, 130, 246, 0.15)' }}>
          
          {timeToStart ? (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto 1.5rem' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: '4px solid #e2e8f0', borderRadius: '50%' }}></div>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: '4px solid #3b82f6', borderRadius: '50%', borderTopColor: 'transparent', animation: 'spin 2s linear infinite' }}></div>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Clock size={24} style={{ color: '#3b82f6', marginBottom: '4px' }} />
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e40af' }}>{timeToStart}</span>
                </div>
              </div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#1e40af', margin: '0 0 0.5rem', letterSpacing: '-0.025em' }}>Scheduled Test</h2>
              <p style={{ color: '#64748b', fontSize: '1rem', margin: 0 }}>Starting very soon...</p>
            </div>
          ) : (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ width: '5rem', height: '5rem', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)', transform: 'rotate(-5deg)' }}>
                <Calculator style={{ width: '2.5rem', height: '2.5rem', color: 'white' }} />
              </div>
              <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#1e40af', margin: '0 0 0.5rem', letterSpacing: '-0.025em' }}>Test Ready!</h2>
              <p style={{ color: '#10b981', fontWeight: 600, fontSize: '1.125rem', margin: 0 }}>The test has officially started</p>
            </div>
          )}

          <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '1.25rem', marginBottom: '2rem', textAlign: 'left', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Game Type</span>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{activeTest.testType.charAt(0).toUpperCase() + activeTest.testType.slice(1)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Duration</span>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{activeTest.duration} Minutes</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Questions</span>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{activeTest.count} Questions</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Difficulty</span>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{activeTest.difficultyLevel}</span>
            </div>
          </div>
          
          {isTestTime ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '12px', padding: '1rem' }}>
                <p style={{ color: '#065f46', fontSize: '0.875rem', margin: 0, lineHeight: 1.5 }}>
                  <strong>Get Ready:</strong> Test is running in secure mode. Stay on this tab and complete all questions.
                </p>
              </div>
              <button
                onClick={handleStartTest}
                style={{ 
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
                  color: 'white', 
                  padding: '1.25rem 2.5rem', 
                  borderRadius: '14px', 
                  fontWeight: 700, 
                  fontSize: '1.25rem', 
                  border: 'none', 
                  cursor: 'pointer', 
                  transition: 'all 0.3s',
                  boxShadow: '0 10px 25px rgba(59, 130, 246, 0.4)'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Start Test Now
              </button>
            </div>
          ) : (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '1.25rem' }}>
              <p style={{ color: '#92400e', fontWeight: 600, margin: '0 0 0.5rem' }}>Waiting for Start Time</p>
              <p style={{ color: '#b45309', fontSize: '0.875rem', margin: 0 }}>
                Scheduled for: {testStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif', padding: '1rem' }}>
      <div style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(226, 232, 240, 0.5)', borderRadius: '20px', padding: '2rem', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)' }}>
        <div style={{ width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
          <Clock style={{ width: '2rem', height: '2rem', color: '#9ca3af' }} />
        </div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#6b7280', margin: '0 0 0.5rem' }}>No Active Tests</h2>
        <p style={{ color: '#9ca3af', margin: '0 0 2rem' }}>Check back later for new assignments.</p>
        
        <div style={{ background: 'linear-gradient(135deg, #f9fafb, #f3f4f6)', border: '1px solid rgba(209, 213, 219, 0.5)', borderRadius: '12px', padding: '1rem', margin: '0 0 1.5rem', textAlign: 'left', fontSize: '0.75rem' }}>
          <p style={{ color: '#374151', margin: '0 0 0.5rem', fontSize: '0.75rem' }}><strong>Debug Info:</strong></p>
          <p style={{ color: '#6b7280', margin: '0.25rem 0', fontSize: '0.75rem' }}>Token: {localStorage.getItem('token') ? 'Present' : 'Missing'}</p>
          <p style={{ color: '#6b7280', margin: '0.25rem 0', fontSize: '0.75rem' }}>Device ID: {localStorage.getItem('deviceId') || 'Missing'}</p>
          <p style={{ color: '#6b7280', margin: '0.25rem 0', fontSize: '0.75rem' }}>User ID: {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user'));
              return u?._id || u?.id || 'Missing';
            } catch(e) { return 'Missing'; }
          })()}</p>
          <p style={{ color: '#6b7280', margin: '0.25rem 0', fontSize: '0.75rem' }}>Last checked: {new Date().toLocaleTimeString()}</p>
        </div>
        
        <button
          onClick={() => window.location.reload()}
          style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default TestContainer;
